import { useMemo, useState } from 'react';
import { ScreenLink as Link } from '../permissions/ScreenLink';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Clock } from 'lucide-react';
import { api } from '../../services/api';
import EmptyState from '../EmptyState';
import { HubPanel, HubSurface, HubTable } from './HubPanel';
import { characterizationHref } from './characterizationModel';
import Kpi from '../ui/Kpi';
import ForwardLink from '../details/ForwardLink';
import { WorkLogsExplorer } from '../worklogs/GroupedWorkLogs';
import { InlineError, Spinner, apiErrorMessage, formatMinutes, useDateFormatter } from '../sales/shared';

const RANGES = [3, 6, 12] as const;

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Work hours logged for this customer, from the canonical work logs
 * (server-side aggregation): totals, per month, per project, recent entries.
 */
export default function WorkHoursTab({ orgId }: { orgId: string }) {
  const { t } = useTranslation();
  const fmtDate = useDateFormatter();
  const [months, setMonths] = useState<(typeof RANGES)[number]>(6);

  const range = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getFullYear(), to.getMonth() - (months - 1), 1);
    return { from: isoDate(from), to: isoDate(to) };
  }, [months]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['customer-work-summary', orgId, range.from, range.to],
    queryFn: () => api.getCustomerWorkSummary(orgId, range),
    enabled: !!orgId,
  });
  const summary = data?.data;
  const maxMonth = Math.max(1, ...(summary?.by_month ?? []).map((m) => m.minutes));

  return (
    <HubPanel
      title={t('organization.tabWorkHours')}
      subtitle={t('organization.workHours.subtitle')}
      action={
        <>
          <ForwardLink to="/work-logs">{t('organization.workHours.openWorkLogs')}</ForwardLink>
          <select className="input py-1.5 w-auto" value={months} aria-label={t('organization.workHours.range')}
            onChange={(e) => setMonths(Number(e.target.value) as (typeof RANGES)[number])}>
            {RANGES.map((r) => <option key={r} value={r}>{t('organization.workHours.lastMonths', { count: r })}</option>)}
          </select>
        </>
      }
    >
      {isLoading ? (
        <Spinner />
      ) : isError || !summary ? (
        <InlineError>{apiErrorMessage(error, t('organization.workHours.loadError'))}</InlineError>
      ) : summary.total_minutes === 0 ? (
        <HubSurface>
          <EmptyState icon={Clock} title={t('organization.workHours.empty')} />
        </HubSurface>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Kpi label={t('organization.workHours.total')} value={formatMinutes(summary.total_minutes)} />
            <Kpi label={t('common.billable')} value={formatMinutes(summary.billable_minutes)} />
            <Kpi label={t('common.nonBillable')} value={formatMinutes(summary.non_billable_minutes)} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <HubSurface padded>
              <h3 className="text-sm font-semibold text-surface-900 mb-3">{t('organization.workHours.byMonth')}</h3>
              <ul className="space-y-2">
                {summary.by_month.map((m) => (
                  <li key={m.month} className="text-sm">
                    <div className="flex justify-between text-xs text-surface-600 mb-0.5">
                      <span dir="ltr">{m.month}</span>
                      <span className="tabular-nums">
                        {formatMinutes(m.minutes)}
                        <span className="text-surface-500"> · {t('common.billable')} {formatMinutes(m.billable_minutes)}</span>
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-surface-100 overflow-hidden" aria-hidden>
                      <div className="h-full bg-primary-600 rounded-full" style={{ width: `${(m.minutes / maxMonth) * 100}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            </HubSurface>
            <HubSurface padded>
              <h3 className="text-sm font-semibold text-surface-900 mb-3">{t('organization.workHours.byProject')}</h3>
              <table className="w-full text-sm">
                <tbody>
                  {summary.by_project.map((p) => (
                    <tr key={p.project_id ?? p.characterization_id ?? 'none'} className="border-b border-surface-100 last:border-0">
                      <td className="py-1.5">
                        {/* P-07 (D-74 §2) — the characterization's hours: their own "אפיון" line. */}
                        {p.characterization_id ? (
                          <Link to={characterizationHref(orgId, p.characterization_id)} className="text-surface-900 hover:text-primary-700 hover:underline underline-offset-2">
                            {t('characterization.cardTitle', { title: p.characterization_title ?? '' })}
                          </Link>
                        ) : p.project_id ? (
                          <Link to={`/projects/${p.project_id}`} className="text-surface-900 hover:text-primary-700 hover:underline underline-offset-2">
                            {p.project_name || t('organization.workHours.unnamedProject')}
                          </Link>
                        ) : (
                          <span className="text-surface-500">{t('organization.workHours.noProject')}</span>
                        )}
                      </td>
                      <td className="py-2 text-end tabular-nums">{formatMinutes(p.minutes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </HubSurface>
          </div>

          {/* D-319 sec.9 - all the customer's logs of the chosen window: "קבץ לפי" + month approval + PDF. */}
          <HubSurface padded>
            <h3 className="text-sm font-semibold text-surface-900 mb-3">{t('logsView.tabTitle')}</h3>
            <WorkLogsExplorer queryKey={`org-${orgId}`} scope={{ organization_id: orgId }} from={range.from} to={range.to} defaultGroupBy="project" />
          </HubSurface>

          <HubSurface>
            <h3 className="text-sm font-semibold text-surface-900 px-4 pt-4 pb-3">{t('organization.workHours.recent')}</h3>
            <HubTable>
              <table className="table-modern border-t border-surface-200">
                <thead>
                  <tr>
                    <th>{t('common.date')}</th>
                    <th className="hidden sm:table-cell">{t('organization.workHours.employee')}</th>
                    <th>{t('organization.workHours.ticket')}</th>
                    <th className="hidden lg:table-cell">{t('common.description')}</th>
                    <th className="text-end">{t('organization.workHours.duration')}</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.recent.map((r) => (
                    <tr key={r.id}>
                      <td className="tabular-nums whitespace-nowrap text-surface-600"><span dir="ltr">{fmtDate(r.start_time, 'dd/MM/yyyy HH:mm')}</span></td>
                      <td className="hidden sm:table-cell">{r.agent_name || '—'}</td>
                      <td className="max-w-xs truncate">
                        {r.ticket_id ? (
                          <Link to={`/tickets/${r.ticket_id}`} className="text-surface-900 hover:text-primary-700 hover:underline underline-offset-2">{r.ticket_title || '—'}</Link>
                        ) : '—'}
                      </td>
                      <td className="hidden lg:table-cell max-w-xs truncate text-surface-600">{r.description || '—'}</td>
                      <td className="text-end whitespace-nowrap tabular-nums">
                        {formatMinutes(r.duration_minutes)}
                        {!r.billable && <span className="ms-1 text-xs text-surface-500">({t('common.nonBillable')})</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </HubTable>
          </HubSurface>
        </div>
      )}
    </HubPanel>
  );
}
